/** Upload - the first place of a bill's journey (ADR-0212, Data in).
 *
 * A title, four tiles from the server's counts, the drop card, one filter line and one table of
 * recent files. The screen *is* the action: there is no Add button because the drop card is it, and
 * the other ways in (email, connected feeds, a spreadsheet) are listed beside the table's filter.
 *
 * What a file becomes is the Processing place's story: stored, checked, read into a draft, then a
 * person confirms the scope, category, vendor and site and accepts. This screen only sends, and shows
 * what happened to what was sent - including a refused file, as a row with the refusal as a chip.
 *
 * Clicking a row opens that file's card (its trail, what was matched, the coverage cell it turns,
 * the result) in a drawer, where "Read it" and "Open review" are, so nothing the old Evidence panel's
 * live card did is lost.
 */

import { UploadSimple } from "@phosphor-icons/react";
import { useState } from "react";

import { useDocuments } from "../api/evidence";
import { stateCount, useDocumentCounts } from "../api/processing";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import {
  Drawer,
  EmptyState,
  FilterLine,
  InfoPopover,
  Tile,
  TablePage,
  TileStrip,
} from "../components/ui";
import type { FilterDef, FilterState } from "../components/ui";
import { EMPTY_FILTERS } from "../components/ui";
import { quantity } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { ScreenFrame } from "./ScreenFrame";
import { useScreen } from "./useScreen";
import { DropZone } from "./upload/DropZone";
import { FileCard } from "./upload/FileCard";
import { OtherWays } from "./upload/OtherWays";
import { RecentFiles, rowOfDocument, rowOfSend } from "./upload/RecentFiles";
import type { FileRow } from "./upload/RecentFiles";
import { useReadDocument } from "./processing/useRead";
import { ReadOutcome } from "./processing/ReadOutcome";
import { useSendFiles } from "./upload/useSendFiles";

const RECENT = 25;

const STAGES: FilterDef = {
  id: "stage",
  label: "Stage",
  mode: "single",
  allLabel: "All stages",
  options: [
    { value: "received", label: "Received" },
    { value: "checked", label: "Checked" },
    { value: "reading", label: "Reading" },
    { value: "read", label: "Read" },
    { value: "matched", label: "Matched" },
    { value: "accepted", label: "Accepted" },
    { value: "refused", label: "Refused" },
  ],
};

const count = (value: number | undefined): string => (value === undefined ? "–" : quantity(String(value)));

function UploadBody() {
  const screen = useScreen();
  const sender = useSendFiles();
  const counts = useDocumentCounts();
  const recent = useDocuments(undefined, { limit: RECENT });
  const reader = useReadDocument();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [open, setOpen] = useState<FileRow | null>(null);

  const documentRows = (recent.data?.rows ?? []).map(rowOfDocument);
  const known = new Set(documentRows.map((row) => row.key));
  const sends = sender.batch.flatMap((entry) => rowOfSend(entry, known) ?? []);
  const all = [...sends, ...documentRows];
  const wanted = filters.values.stage?.[0];
  const needle = filters.search.trim().toLowerCase();
  const rows = all.filter(
    (row) =>
      (!wanted || row.chip.stage === wanted) && (!needle || row.name.toLowerCase().includes(needle)),
  );

  // Nothing sent, here or before: the filter line has nothing to filter, so it gives way to the table's
  // empty state and its two explanations move to the title card.
  const nothingSent = !recent.isPending && !recent.error && all.length === 0;

  const receivedAll = counts.data?.documents;
  const inProgress = stateCount(counts.data, "received", "validated", "extracting");

  return (
    <ScreenFrame screen={screen} title="Upload">
      <TablePage
        icon={UploadSimple}
        title="Upload"
        label="Data in"
        tiles={
          counts.error ? (
            <ReadFailed of="The document counts" error={counts.error} onRetry={() => void counts.refetch()} />
          ) : (
          <TileStrip label="Files">
            <Tile label="Received" value={count(receivedAll)} loading={counts.isPending}
              testId="tile-received" />
            <Tile label="Not yet read" value={count(inProgress)} loading={counts.isPending}
              testId="tile-reading" />
            <Tile
              label="To review"
              value={count(stateCount(counts.data, "awaiting_review"))}
              loading={counts.isPending}
              testId="tile-review"
            />
            <Tile label="In ledger" value={count(stateCount(counts.data, "verified"))} loading={counts.isPending}
              testId="tile-ledger" />
          </TileStrip>
          )
        }
        extra={
          <>
            <DropZone sender={sender} method={screen.method} />
            <OtherWays
              mayReadFeeds={
                sender.me ? ["admin", "approver", "contributor", "viewer"].includes(sender.me.role) : false
              }
              mayManageFeeds={sender.me?.role === "admin"}
            />
          </>
        }
        actions={
          nothingSent ? (
            <>
              <InfoPopover infoKey="upload.how-it-works" />
              <InfoPopover infoKey="upload.other-ways" />
            </>
          ) : undefined
        }
        filters={
          nothingSent ? undefined : (
          <FilterLine
            filters={[STAGES]}
            state={filters}
            onChange={setFilters}
            resultCount={
              recent.data
                ? { shown: rows.length, total: Math.max(recent.data.total, rows.length), noun: "files" }
                : undefined
            }
            actions={
              <>
                <InfoPopover infoKey="upload.stages" />
                <InfoPopover infoKey="upload.how-it-works" />
                <InfoPopover infoKey="upload.other-ways" />
                {reader.outcome ? <ReadOutcome outcome={reader.outcome} /> : null}
              </>
            }
          />
          )
        }
        table={
          recent.error ? (
            <ReadFailed of="Recent files" error={recent.error} onRetry={() => void recent.refetch()} />
          ) : (
            <RecentFiles
              rows={rows}
              loading={recent.isPending}
              onOpen={setOpen}
              empty={
                <EmptyState icon={UploadSimple} title="No files yet" compact testId="recent-empty">
                  Files you send appear here as they are read.
                </EmptyState>
              }
            />
          )
        }
      />
      <Drawer open={open !== null} onClose={() => setOpen(null)} title={open?.name ?? "File"} width="lg">
        {open?.documentId ? (
          <FileCard
            documentId={open.documentId}
            fileName={open.name}
            window={screen.window}
            mayRead={sender.mayUpload === true}
            reading={reader.pending && reader.target === open.documentId}
            onRead={(id) => void reader.read(id)}
            method={screen.method}
          />
        ) : null}
        <WriteFailed error={reader.refusal} of="the reading" onReload={reader.clearRefusal} />
      </Drawer>
    </ScreenFrame>
  );
}

export function UploadScreen() {
  return (
    <RequireSession>
      <UploadBody />
    </RequireSession>
  );
}
